// The Customers tab's filter card.
//
// The shape is TicketFilters.jsx's, deliberately: same container, same reserved
// hint line, same "Clear filters" behaviour, so the two screens do not each teach
// their own filter bar. Every control maps to a documented query param on
// GET /api/customers/list and nothing else — an unrecognised param is 400
// INVALID_QUERY_PARAM on purpose, so this component is the whole vocabulary.
//
// THE SEARCH BOX IS ONE BOX WITH NO MODE SWITCH, and that is the point of the whole
// screen. SPEC.md line 294: "agents ask for a store or account number, or a
// person's name/email." The server resolves an account number (`BB-1001`), a phone
// number in whatever format the caller reads it out (`5550142` finds `555-0142`), a
// name, an email or a company from the same term. Asking the agent to first
// classify what the caller just said costs a second of silence on the call, which
// is the thing this screen is optimised against.

import { useEffect, useState } from 'react';

import OrganizationPicker from './OrganizationPicker.jsx';
import { DEFAULT_SORT, hasActiveFilters } from './customer-query.js';

const SORTS = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: '-last_ticket_at', label: 'Recently in touch' },
];

/**
 * @param {object} props
 * @param {object} props.filters   From readFilters() — the URL is the source of truth.
 * @param {(patch: object) => void} props.onChange
 * @param {Array} props.locations  GET /api/organizations/:id/locations, or [].
 * @param {string} props.locationStatus  'idle' | 'loading' | 'ready' | 'error'.
 */
export default function CustomerFilters({ filters, onChange, locations, locationStatus }) {
  const [draftQuery, setDraftQuery] = useState(filters.q);

  // The URL is the source of truth, so a back/forward navigation or a cleared
  // filter has to pull the text box back in line with it.
  useEffect(() => {
    setDraftQuery(filters.q);
  }, [filters.q]);

  const trimmed = draftQuery.trim();
  // Exactly one character: `q` shorter than two is 400 INVALID_QUERY_PARAM, so it
  // is never sent. Zero characters is not too short, it is no search at all.
  const queryTooShort = trimmed.length === 1;

  function submitQuery(event) {
    event.preventDefault();
    if (queryTooShort) return;
    onChange({ q: trimmed });
  }

  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      {/* items-start, not items-end: the search column is taller than the pickers
          because it reserves a line below the input for the hint. Bottom-aligning
          would push the Search label above the others. */}
      <div className="flex flex-wrap items-start gap-3">
        <form onSubmit={submitQuery} className="min-w-64 flex-1">
          <label className="field-label" htmlFor="customer-search">
            Find a caller
          </label>
          <div className="flex gap-2">
            <input
              id="customer-search"
              type="search"
              className="field-input"
              placeholder="Account number, phone, name, email or company"
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
            />
            <button
              type="submit"
              className="btn-ghost h-10 shrink-0 ring-1 ring-inset ring-line-strong"
              disabled={queryTooShort}
            >
              Search
            </button>
          </div>
          {/* Reserved height whether or not it says anything, so the card does not
              jump the table down the page on the second keystroke. */}
          <p className="mt-1 h-4 text-xs text-ink-muted">
            {queryTooShort ? 'Type at least two characters.' : ''}
          </p>
        </form>

        <OrganizationPicker
          organizationId={filters.organizationId}
          organizationName={filters.organizationName}
          // Changing the organization always clears the store: a location under the
          // old organization would scope the list to a store the new organization
          // does not have, and silently return nothing.
          onChange={(next) => onChange({ ...next, locationId: '' })}
        />

        {/* Rendered only while an organization is selected. GET /api/organizations/
            :id/locations is deliberately unpaginated and returns the whole list, so
            this one can be an ordinary <select>; with no organization there is
            nothing to choose from and an empty dropdown would just be a dead
            control. */}
        {filters.organizationId && (
          <div className="w-52">
            <label className="field-label" htmlFor="customer-location">
              Store
            </label>
            <select
              id="customer-location"
              className="field-input"
              value={filters.locationId}
              onChange={(event) => onChange({ locationId: event.target.value })}
              disabled={locationStatus === 'loading'}
            >
              <option value="">All stores</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.account_number
                    ? `${location.name} · ${location.account_number}`
                    : location.name}
                </option>
              ))}
            </select>
            <p className="mt-1 h-4 text-xs text-ink-muted">
              {locationStatus === 'error' ? 'Store list unavailable.' : ''}
            </p>
          </div>
        )}

        <div className="w-52">
          <label className="field-label" htmlFor="customer-sort">
            Sort
          </label>
          <select
            id="customer-sort"
            className="field-input"
            value={filters.sort || DEFAULT_SORT}
            onChange={(event) => onChange({ sort: event.target.value })}
          >
            {SORTS.map((sort) => (
              <option key={sort.value} value={sort.value}>
                {sort.label}
              </option>
            ))}
          </select>
          <p className="mt-1 h-4 text-xs text-ink-muted" />
        </div>
      </div>

      {/* Sort is not a filter and is deliberately not cleared here — clearing
          filters must not silently reorder the table under the agent. */}
      {hasActiveFilters(filters) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line pt-3">
          <button
            type="button"
            className="text-xs font-medium text-brand-700 hover:underline dark:text-brand-300"
            onClick={() =>
              onChange({ q: '', organizationId: '', organizationName: '', locationId: '' })
            }
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
